/**
 * Organization Customization Types
 * 
 * This file defines types for organization-specific customization
 * based on activity center type (dance, gymnastics, music, camp, multi)
 */

// Organization Types
export type OrganizationType = 'dance' | 'gymnastics' | 'music' | 'camp' | 'multi' | 'swim' | 'martial_arts' | 'equestrian' | 'afterschool';

// Feature Modules that can be enabled/disabled
export interface FeatureModules {
  // Core Features
  classManagement: boolean;
  studentManagement: boolean;
  familyManagement: boolean;
  staffManagement: boolean;
  billing: boolean;
  attendance: boolean;
  
  // Advanced Features
  waitlist: boolean;
  makeupClasses: boolean;
  recitals: boolean;
  competitions: boolean;
  camps: boolean;
  privateInstruction: boolean;
  groupLessons: boolean;
  
  // Communication
  messaging: boolean;
  notifications: boolean;
  newsletters: boolean;
  
  // Commerce
  apparelShop: boolean;
  onlinePayments: boolean;
  invoicing: boolean;
  recurringBilling: boolean;
  
  // Parent Portal
  parentPortal: boolean;
  onlineEnrollment: boolean;
  documentSharing: boolean;
  
  // Reporting
  advancedReporting: boolean;
  aiAssistant: boolean;
  customReports: boolean;
  
  // Industry-Specific
  recitalManagement?: boolean; // Dance/Music
  meetManagement?: boolean; // Gymnastics/Swim
  badgeTracking?: boolean; // Camps
  beltProgression?: boolean; // Martial Arts
  horseManagement?: boolean; // Equestrian
  homeworkTracking?: boolean; // Afterschool
}

// Terminology customization based on industry
export interface TerminologyPreferences {
  // Core Terms
  student: string; // e.g., "dancer", "gymnast", "musician", "camper", "rider"
  students: string; // plural form
  class: string; // e.g., "class", "lesson", "session", "activity"
  classes: string; // plural form
  instructor: string; // e.g., "teacher", "coach", "counselor", "instructor"
  instructors: string; // plural form
  
  // Activity Terms
  enrollment: string; // e.g., "enrollment", "registration", "sign-up"
  schedule: string; // e.g., "schedule", "calendar", "timetable"
  performance: string; // e.g., "recital", "showcase", "concert", "meet", "show"
  
  // Location Terms
  location: string; // e.g., "studio", "gym", "facility", "camp", "stable"
  room: string; // e.g., "studio", "mat", "room", "arena", "field"
  
  // Progress Terms
  level: string; // e.g., "level", "grade", "belt", "badge"
  achievement: string; // e.g., "achievement", "skill", "badge", "certificate"
  
  // Session Terms
  season: string; // e.g., "season", "term", "session", "semester"
  trial: string; // e.g., "trial class", "free lesson", "intro session"
}

// Industry-specific settings
export interface IndustrySpecificSettings {
  // Dance-specific
  dance?: {
    defaultDanceStyles: string[];
    competitionTracking: boolean;
    costumeManagement: boolean;
    recitalPlanning: boolean;
    examinations: boolean;
  };
  
  // Gymnastics-specific
  gymnastics?: {
    apparatusTypes: string[];
    skillProgressionTracking: boolean;
    meetRegistration: boolean;
    teamManagement: boolean;
    usagMembership: boolean;
  };
  
  // Music-specific
  music?: {
    instrumentTypes: string[];
    practiceLogTracking: boolean;
    recitalManagement: boolean;
    ensembleManagement: boolean;
    musicLibrary: boolean;
    theoryTracking: boolean;
  };
  
  // Camp-specific
  camp?: {
    sessionTypes: string[]; // day, overnight, specialty
    medicalFormRequirements: boolean;
    pickupAuthorization: boolean;
    activityScheduling: boolean;
    mealPlanning: boolean;
    cabinAssignments: boolean;
  };
  
  // Swim-specific
  swim?: {
    poolLocations: string[];
    swimLevels: string[];
    waterSafetyCompliance: boolean;
    laneAssignments: boolean;
    strokeProgression: boolean;
  };
  
  // Martial Arts-specific
  martialArts?: {
    beltSystem: string[];
    testingSchedule: boolean;
    sparringTracking: boolean;
    tournamentManagement: boolean;
    weaponsTraining: boolean;
  };
  
  // Equestrian-specific
  equestrian?: {
    horseManagement: boolean;
    lessonTypes: string[]; // private, group, trail
    tackTracking: boolean;
    showRegistration: boolean;
    stableManagement: boolean;
  };
  
  // After School-specific
  afterschool?: {
    pickupManagement: boolean;
    homeworkTracking: boolean;
    snackProgram: boolean;
    transportationRoutes: boolean;
    gradeTracking: boolean;
  };
}

// UI/UX Customization
export interface UICustomization {
  theme: {
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
    mode: 'light' | 'dark' | 'auto';
  };
  
  branding: {
    logoUrl?: string;
    faviconUrl?: string;
    bannerImageUrl?: string;
    customCSS?: string;
  };
  
  layout: {
    sidebarPosition: 'left' | 'right';
    navigationStyle: 'sidebar' | 'top' | 'both';
    defaultView: 'dashboard' | 'calendar' | 'list';
  };
  
  dashboardWidgets: {
    enabled: string[]; // widget IDs
    layout: Array<{
      widgetId: string;
      position: { x: number; y: number; w: number; h: number };
    }>;
  };
}

// Workflow Customization
export interface WorkflowSettings {
  enrollment: {
    requireApproval: boolean;
    autoWaitlist: boolean;
    requireDeposit: boolean;
    trialClassPolicy: 'free' | 'paid' | 'none';
    enrollmentDeadlineDays?: number;
  };
  
  payments: {
    acceptedMethods: ('card' | 'ach' | 'cash' | 'check')[];
    autoCharge: boolean;
    paymentPlans: boolean;
    lateFeePolicy?: {
      enabled: boolean;
      amount: number;
      gracePeriodDays: number;
    };
  };
  
  attendance: {
    trackingMethod: 'manual' | 'qr' | 'biometric' | 'parent-app';
    makeupPolicy: {
      allowed: boolean;
      expirationDays?: number;
      maxPerSession?: number;
    };
    tardyThresholdMinutes?: number;
  };
  
  communication: {
    defaultChannels: ('email' | 'sms' | 'app' | 'portal')[];
    automatedReminders: {
      classReminders: boolean;
      paymentReminders: boolean;
      eventReminders: boolean;
      advanceNoticeDays: number;
    };
  };
}

// Complete Organization Settings
export interface OrganizationCustomizationSettings {
  // Basic Information
  organizationId: string;
  organizationType: OrganizationType;
  
  // Feature Configuration
  enabledModules: FeatureModules;
  
  // Terminology
  terminology: TerminologyPreferences;
  
  // Industry-Specific
  industrySettings: IndustrySpecificSettings;
  
  // UI/UX
  uiCustomization: UICustomization;
  
  // Workflows
  workflowSettings: WorkflowSettings;
  
  // Metadata
  createdAt: string;
  updatedAt: string;
  version: string;
}

// Preset configurations for different organization types
export const ORGANIZATION_PRESETS: Record<OrganizationType, Partial<OrganizationCustomizationSettings>> = {
  dance: {
    enabledModules: {
      classManagement: true,
      studentManagement: true,
      familyManagement: true,
      staffManagement: true,
      billing: true,
      attendance: true,
      waitlist: true,
      makeupClasses: true,
      recitals: true,
      competitions: true,
      camps: false,
      privateInstruction: true,
      groupLessons: true,
      messaging: true,
      notifications: true,
      newsletters: true,
      apparelShop: true,
      onlinePayments: true,
      invoicing: true,
      recurringBilling: true,
      parentPortal: true,
      onlineEnrollment: true,
      documentSharing: true,
      advancedReporting: true,
      aiAssistant: true,
      customReports: true,
      recitalManagement: true,
    },
    terminology: {
      student: 'dancer',
      students: 'dancers',
      class: 'class',
      classes: 'classes',
      instructor: 'teacher',
      instructors: 'teachers',
      enrollment: 'enrollment',
      schedule: 'schedule',
      performance: 'recital',
      location: 'studio',
      room: 'studio',
      level: 'level',
      achievement: 'achievement',
      season: 'season',
      trial: 'trial class',
    },
    industrySettings: {
      dance: {
        defaultDanceStyles: ['Ballet', 'Jazz', 'Tap', 'Hip Hop', 'Contemporary', 'Lyrical'],
        competitionTracking: true,
        costumeManagement: true,
        recitalPlanning: true,
        examinations: true,
      },
    },
  },
  
  gymnastics: {
    enabledModules: {
      classManagement: true,
      studentManagement: true,
      familyManagement: true,
      staffManagement: true,
      billing: true,
      attendance: true,
      waitlist: true,
      makeupClasses: true,
      recitals: false,
      competitions: true,
      camps: true,
      privateInstruction: true,
      groupLessons: true,
      messaging: true,
      notifications: true,
      newsletters: true,
      apparelShop: true,
      onlinePayments: true,
      invoicing: true,
      recurringBilling: true,
      parentPortal: true,
      onlineEnrollment: true,
      documentSharing: true,
      advancedReporting: true,
      aiAssistant: true,
      customReports: true,
      meetManagement: true,
    },
    terminology: {
      student: 'gymnast',
      students: 'gymnasts',
      class: 'class',
      classes: 'classes',
      instructor: 'coach',
      instructors: 'coaches',
      enrollment: 'registration',
      schedule: 'schedule',
      performance: 'meet',
      location: 'gym',
      room: 'floor',
      level: 'level',
      achievement: 'skill',
      season: 'season',
      trial: 'trial class',
    },
    industrySettings: {
      gymnastics: {
        apparatusTypes: ['Floor', 'Vault', 'Bars', 'Beam', 'Rings', 'Pommel Horse'],
        skillProgressionTracking: true,
        meetRegistration: true,
        teamManagement: true,
        usagMembership: true,
      },
    },
  },
  
  music: {
    enabledModules: {
      classManagement: true,
      studentManagement: true,
      familyManagement: true,
      staffManagement: true,
      billing: true,
      attendance: true,
      waitlist: true,
      makeupClasses: true,
      recitals: true,
      competitions: true,
      camps: false,
      privateInstruction: true,
      groupLessons: true,
      messaging: true,
      notifications: true,
      newsletters: true,
      apparelShop: false,
      onlinePayments: true,
      invoicing: true,
      recurringBilling: true,
      parentPortal: true,
      onlineEnrollment: true,
      documentSharing: true,
      advancedReporting: true,
      aiAssistant: true,
      customReports: true,
      recitalManagement: true,
    },
    terminology: {
      student: 'student',
      students: 'students',
      class: 'lesson',
      classes: 'lessons',
      instructor: 'teacher',
      instructors: 'teachers',
      enrollment: 'enrollment',
      schedule: 'schedule',
      performance: 'recital',
      location: 'school',
      room: 'room',
      level: 'grade',
      achievement: 'certificate',
      season: 'semester',
      trial: 'trial lesson',
    },
    industrySettings: {
      music: {
        instrumentTypes: ['Piano', 'Guitar', 'Violin', 'Voice', 'Drums', 'Cello', 'Flute'],
        practiceLogTracking: true,
        recitalManagement: true,
        ensembleManagement: true,
        musicLibrary: true,
        theoryTracking: true,
      },
    },
  },
  
  camp: {
    enabledModules: {
      classManagement: true,
      studentManagement: true,
      familyManagement: true,
      staffManagement: true,
      billing: true,
      attendance: true,
      waitlist: true,
      makeupClasses: false,
      recitals: false,
      competitions: false,
      camps: true,
      privateInstruction: false,
      groupLessons: false,
      messaging: true,
      notifications: true,
      newsletters: true,
      apparelShop: true,
      onlinePayments: true,
      invoicing: true,
      recurringBilling: false,
      parentPortal: true,
      onlineEnrollment: true,
      documentSharing: true,
      advancedReporting: true,
      aiAssistant: true,
      customReports: true,
      badgeTracking: true,
    },
    terminology: {
      student: 'camper',
      students: 'campers',
      class: 'activity',
      classes: 'activities',
      instructor: 'counselor',
      instructors: 'counselors',
      enrollment: 'registration',
      schedule: 'calendar',
      performance: 'showcase',
      location: 'camp',
      room: 'area',
      level: 'age group',
      achievement: 'badge',
      season: 'session',
      trial: 'open house',
    },
    industrySettings: {
      camp: {
        sessionTypes: ['Day Camp', 'Overnight Camp', 'Specialty Camp', 'Sports Camp'],
        medicalFormRequirements: true,
        pickupAuthorization: true,
        activityScheduling: true,
        mealPlanning: true,
        cabinAssignments: true,
      },
    },
  },
  
  multi: {
    enabledModules: {
      classManagement: true,
      studentManagement: true,
      familyManagement: true,
      staffManagement: true,
      billing: true,
      attendance: true,
      waitlist: true,
      makeupClasses: true,
      recitals: true,
      competitions: true,
      camps: true,
      privateInstruction: true,
      groupLessons: true,
      messaging: true,
      notifications: true,
      newsletters: true,
      apparelShop: true,
      onlinePayments: true,
      invoicing: true,
      recurringBilling: true,
      parentPortal: true,
      onlineEnrollment: true,
      documentSharing: true,
      advancedReporting: true,
      aiAssistant: true,
      customReports: true,
    },
    terminology: {
      student: 'student',
      students: 'students',
      class: 'class',
      classes: 'classes',
      instructor: 'instructor',
      instructors: 'instructors',
      enrollment: 'enrollment',
      schedule: 'schedule',
      performance: 'event',
      location: 'location',
      room: 'room',
      level: 'level',
      achievement: 'achievement',
      season: 'session',
      trial: 'trial class',
    },
    industrySettings: {},
  },
  
  swim: {
    enabledModules: {
      classManagement: true,
      studentManagement: true,
      familyManagement: true,
      staffManagement: true,
      billing: true,
      attendance: true,
      waitlist: true,
      makeupClasses: true,
      recitals: false,
      competitions: true,
      camps: true,
      privateInstruction: true,
      groupLessons: true,
      messaging: true,
      notifications: true,
      newsletters: true,
      apparelShop: true,
      onlinePayments: true,
      invoicing: true,
      recurringBilling: true,
      parentPortal: true,
      onlineEnrollment: true,
      documentSharing: true,
      advancedReporting: true,
      aiAssistant: true,
      customReports: true,
      meetManagement: true,
    },
    terminology: {
      student: 'swimmer',
      students: 'swimmers',
      class: 'lesson',
      classes: 'lessons',
      instructor: 'instructor',
      instructors: 'instructors',
      enrollment: 'registration',
      schedule: 'schedule',
      performance: 'swim meet',
      location: 'pool',
      room: 'lane',
      level: 'level',
      achievement: 'badge',
      season: 'session',
      trial: 'assessment',
    },
    industrySettings: {
      swim: {
        poolLocations: ['Main Pool', 'Training Pool', 'Outdoor Pool'],
        swimLevels: ['Beginner', 'Advanced Beginner', 'Intermediate', 'Advanced', 'Competitive'],
        waterSafetyCompliance: true,
        laneAssignments: true,
        strokeProgression: true,
      },
    },
  },
  
  martial_arts: {
    enabledModules: {
      classManagement: true,
      studentManagement: true,
      familyManagement: true,
      staffManagement: true,
      billing: true,
      attendance: true,
      waitlist: true,
      makeupClasses: true,
      recitals: false,
      competitions: true,
      camps: true,
      privateInstruction: true,
      groupLessons: true,
      messaging: true,
      notifications: true,
      newsletters: true,
      apparelShop: true,
      onlinePayments: true,
      invoicing: true,
      recurringBilling: true,
      parentPortal: true,
      onlineEnrollment: true,
      documentSharing: true,
      advancedReporting: true,
      aiAssistant: true,
      customReports: true,
      beltProgression: true,
    },
    terminology: {
      student: 'student',
      students: 'students',
      class: 'class',
      classes: 'classes',
      instructor: 'sensei',
      instructors: 'instructors',
      enrollment: 'enrollment',
      schedule: 'schedule',
      performance: 'tournament',
      location: 'dojo',
      room: 'mat',
      level: 'belt',
      achievement: 'rank',
      season: 'term',
      trial: 'intro class',
    },
    industrySettings: {
      martialArts: {
        beltSystem: ['White', 'Yellow', 'Orange', 'Green', 'Blue', 'Brown', 'Black'],
        testingSchedule: true,
        sparringTracking: true,
        tournamentManagement: true,
        weaponsTraining: true,
      },
    },
  },
  
  equestrian: {
    enabledModules: {
      classManagement: true,
      studentManagement: true,
      familyManagement: true,
      staffManagement: true,
      billing: true,
      attendance: true,
      waitlist: true,
      makeupClasses: true,
      recitals: false,
      competitions: true,
      camps: true,
      privateInstruction: true,
      groupLessons: true,
      messaging: true,
      notifications: true,
      newsletters: true,
      apparelShop: true,
      onlinePayments: true,
      invoicing: true,
      recurringBilling: true,
      parentPortal: true,
      onlineEnrollment: true,
      documentSharing: true,
      advancedReporting: true,
      aiAssistant: true,
      customReports: true,
      horseManagement: true,
    },
    terminology: {
      student: 'rider',
      students: 'riders',
      class: 'lesson',
      classes: 'lessons',
      instructor: 'instructor',
      instructors: 'instructors',
      enrollment: 'registration',
      schedule: 'schedule',
      performance: 'horse show',
      location: 'stable',
      room: 'arena',
      level: 'level',
      achievement: 'ribbon',
      season: 'season',
      trial: 'evaluation ride',
    },
    industrySettings: {
      equestrian: {
        horseManagement: true,
        lessonTypes: ['Private', 'Semi-Private', 'Group', 'Trail Ride'],
        tackTracking: true,
        showRegistration: true,
        stableManagement: true,
      },
    },
  },
  
  afterschool: {
    enabledModules: {
      classManagement: true,
      studentManagement: true,
      familyManagement: true,
      staffManagement: true,
      billing: true,
      attendance: true,
      waitlist: true,
      makeupClasses: false,
      recitals: false,
      competitions: false,
      camps: false,
      privateInstruction: false,
      groupLessons: false,
      messaging: true,
      notifications: true,
      newsletters: true,
      apparelShop: false,
      onlinePayments: true,
      invoicing: true,
      recurringBilling: true,
      parentPortal: true,
      onlineEnrollment: true,
      documentSharing: true,
      advancedReporting: true,
      aiAssistant: true,
      customReports: true,
      homeworkTracking: true,
    },
    terminology: {
      student: 'student',
      students: 'students',
      class: 'program',
      classes: 'programs',
      instructor: 'staff',
      instructors: 'staff members',
      enrollment: 'enrollment',
      schedule: 'schedule',
      performance: 'showcase',
      location: 'center',
      room: 'room',
      level: 'grade',
      achievement: 'award',
      season: 'semester',
      trial: 'tour',
    },
    industrySettings: {
      afterschool: {
        pickupManagement: true,
        homeworkTracking: true,
        snackProgram: true,
        transportationRoutes: true,
        gradeTracking: true,
      },
    },
  },
};

// Helper function to get customization for organization type
export function getOrganizationCustomization(
  organizationType: OrganizationType
): Partial<OrganizationCustomizationSettings> {
  return ORGANIZATION_PRESETS[organizationType] || ORGANIZATION_PRESETS.multi;
}

// Helper function to merge custom settings with preset
export function mergeOrganizationSettings(
  organizationType: OrganizationType,
  customSettings: Partial<OrganizationCustomizationSettings>
): OrganizationCustomizationSettings {
  const preset = getOrganizationCustomization(organizationType);
  
  return {
    organizationId: customSettings.organizationId || '',
    organizationType,
    enabledModules: {
      ...preset.enabledModules,
      ...customSettings.enabledModules,
    } as FeatureModules,
    terminology: {
      ...preset.terminology,
      ...customSettings.terminology,
    } as TerminologyPreferences,
    industrySettings: {
      ...preset.industrySettings,
      ...customSettings.industrySettings,
    } as IndustrySpecificSettings,
    uiCustomization: customSettings.uiCustomization || {
      theme: {
        primaryColor: '#3B82F6',
        secondaryColor: '#10B981',
        accentColor: '#F59E0B',
        mode: 'light',
      },
      branding: {},
      layout: {
        sidebarPosition: 'left',
        navigationStyle: 'sidebar',
        defaultView: 'dashboard',
      },
      dashboardWidgets: {
        enabled: [],
        layout: [],
      },
    },
    workflowSettings: customSettings.workflowSettings || {
      enrollment: {
        requireApproval: false,
        autoWaitlist: true,
        requireDeposit: false,
        trialClassPolicy: 'free',
      },
      payments: {
        acceptedMethods: ['card', 'ach'],
        autoCharge: false,
        paymentPlans: true,
      },
      attendance: {
        trackingMethod: 'manual',
        makeupPolicy: {
          allowed: true,
          expirationDays: 30,
          maxPerSession: 2,
        },
      },
      communication: {
        defaultChannels: ['email'],
        automatedReminders: {
          classReminders: true,
          paymentReminders: true,
          eventReminders: true,
          advanceNoticeDays: 1,
        },
      },
    },
    createdAt: customSettings.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    version: '1.0.0',
  };
}